import { Text, View } from '@react-pdf/renderer';

import { Disease, DiseaseFullName } from 'src/models/Disease.model';
import { handleNSI18Next } from 'src/models/Language.model';
import { ReportType } from 'src/models/ReportType.model';
import { Session } from 'src/models/Session.model';

import getCvdRankData from 'src/utils/getCvdRankData';
import getCvdRiskData from 'src/utils/getCvdRiskData';
import parseStrongTags from 'src/utils/parseStrongTags';
import { cls } from 'src/utils/reactPdfStyles';

import BadgeWithIcon from './BadgeWithIcon';

interface ICvdSectionProps {
  session: Session;
  disease: Disease;
  type: ReportType;
  showDiseaseName: boolean;
  showNotes: boolean;
}

const CvdSection = ({
  session,
  disease,
  type,
  showDiseaseName,
  showNotes,
}: ICvdSectionProps) => {
  const { handleCommonT, handleReportT } = handleNSI18Next();

  const rankData = getCvdRankData(session, disease);

  const riskData = getCvdRiskData(session, disease);

  return (
    <View style={cls('my-4')}>
      <View style={cls('d-flex hstack justify-content-between')}>
        {showDiseaseName && (
          <View style={cls('td px-0 border-0 w-20')}>
            <Text style={cls('text-center align-middle strong pt-4')}>
              {handleReportT(`classificationSection.${disease}.caption`)}
            </Text>
          </View>
        )}

        <View style={cls('d-flex hstack justify-content-between')}>
          <View style={cls('td w-30 b')}>
            <Text>{handleReportT('cvdSection.indicator')}</Text>
          </View>
          <View style={cls('td w-20 b')}>
            <Text>{handleReportT('cvdSection.result')}</Text>
          </View>
          <View style={cls('td w-50 b')}>
            <Text>{handleReportT('cvdSection.explanation')}</Text>
          </View>
        </View>
      </View>

      <View style={cls('d-flex hstack justify-content-between')}>
        <View style={cls('td w-30')}>
          <Text style={cls('strong')}>
            {handleCommonT(
              `diseaseFullName.${DiseaseFullName(Disease.WHOCVD_RISK)}`,
            )}
            :
          </Text>
        </View>

        <View style={cls('td w-20')}>
          <BadgeWithIcon
            icon={riskData.icon}
            text={riskData.iconText}
            className={riskData.className}
          />
        </View>
        <View style={cls('td w-50')}>
          <Text>
            {parseStrongTags(riskData.desc).map((segment, i) => (
              <Text key={i} style={segment.bold ? cls('bold') : undefined}>
                {segment.text}
              </Text>
            ))}
          </Text>
          {riskData.disclaimer && (
            <Text style={cls('mt-1 text-muted i')}>{riskData.disclaimer}</Text>
          )}
        </View>
      </View>

      <View style={cls('d-flex hstack justify-content-between')}>
        <View style={cls('td w-30')}>
          {
            <Text style={cls('strong')}>
              {handleCommonT(
                `diseaseFullName.${DiseaseFullName(Disease.WHOCVD_RANK)}`,
              )}
              :
            </Text>
          }
        </View>
        <View style={cls('td w-20')}>
          <BadgeWithIcon
            icon={rankData.icon}
            text={rankData.iconText}
            className={rankData.className}
          />
        </View>
        <View style={cls('td w-50')}>
          <Text>{rankData.desc}</Text>
        </View>
      </View>

      {riskData.shouldShowRiskRecommendation && (
        <View
          style={cls(
            'mt-4 rounded bg-warning-100 semibold text-warning-200 p-3 mb-0',
            {
              border: '1px solid #F5E5D2',
              borderLeft: '3px solid #A56222',
            },
          )}
        >
          <Text>{handleReportT('cvdSection.riskRecommendation')}</Text>
        </View>
      )}

      {showNotes && (
        <View style={cls('report-text mt-1')}>
          <View style={cls('py-3 px-3')}>
            {type === ReportType.CLINICAL ? (
              <Text>{handleReportT('cvdSection.referToTheEndText')}</Text>
            ) : (
              <Text>
                <Text style={cls('mt-1')}>
                  {handleReportT('resultSection.patient.cvdContactText')}
                </Text>
                <Text style={cls('mt-2')}>
                  {handleReportT(
                    'resultSection.patient.artifactWarningTextAlt',
                  )}
                </Text>
              </Text>
            )}
          </View>
        </View>
      )}
    </View>
  );
};

export default CvdSection;
